<template>
  <div>
    <!-- Search + Add button row -->
    <div
      class="row"
      style="margin: 0px 0px 20px 0px"
    >
      <b-form-input
        type="search"
        class="col"
        autofocus
        :value="filter"
        @input="$emit('update:filter', $event)"
        placeholder="ค้นหาใบขอซื้อ จากรหัสใบขอซื้อ หรือรหัสใบวางแผนความต้องการวัสดุ"
      ></b-form-input>
      <b-button
        class="ml-3 add-pr-btn"
        @click="$emit('add-pr')"
      >
        <i class="fas fa-plus"></i> เพิ่มใบขอซื้อโดยตรง
      </b-button>
    </div>

    <!-- PR table -->
    <b-table
      :id="tableId"
      hover
      small
      sort-icon-left
      head-variant="light"
      sort-by="creationDt"
      :items="items"
      :fields="prFields"
      :filter="filter"
      :filter-included-fields="['no', 'mrpNo']"
      :sort-desc="true"
      :sort-compare="sortCompare"
      :responsive="true"
    >
      <template v-slot:cell(index)="row">{{ row.index + 1 }}.</template>

      <template v-slot:cell(status)="row">
        <PrStatusBadge :status="row.item.status" />
      </template>

      <template v-slot:cell(creationDt)="row">{{
        formatDate(row.item.creationDt)
      }}</template>

      <template v-slot:cell(buttons)="row">
        <ActionButtonGroup
          :item="row.item"
          :show-delete="showCancel"
          :disabledDelete="showCancel && !isPrCancelable(row.item)"
          deleteTooltip="ลบใบขอซื้อ"
          @delete="$emit('cancel', row.item)"
          :show-edit="showEdit"
          :disabledEdit="showEdit && !isPrEditable(row.item)"
          editTooltip="แก้ไขใบขอซื้อ"
          @edit="$emit('edit', row.item)"
          :show-info="true"
          infoTooltip="ดูข้อมูลใบขอซื้อ"
          @info="$emit('info', row.item)"
        />
      </template>
    </b-table>
  </div>
</template>

<script>
import moment from "moment";
import ActionButtonGroup from "@/components/table/stock/button/ActionButtons.vue";
import PrStatusBadge from "@/components/badge/PrStatusBadge.vue";
import { PR_STATUS } from "@/constants/productStock";

export default {
  name: "PrListTab",
  components: {
    ActionButtonGroup,
    PrStatusBadge,
  },
  props: {
    /** Unique HTML id for the b-table (required to avoid duplicate id warnings) */
    tableId: {
      type: String,
      required: true,
    },
    /** Array of PR items to display */
    items: {
      type: Array,
      default: () => [],
    },
    /** Current search filter text (supports .sync via update:filter event) */
    filter: {
      type: String,
      default: "",
    },
    /** Whether to show the edit button column */
    showEdit: {
      type: Boolean,
      default: true,
    },
    /** Whether to show the cancel (trash) button */
    showCancel: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:filter", "add-pr", "edit", "info", "cancel"],
  data() {
    return {
      prFields: [
        {
          key: "index",
          label: "",
          thStyle: { width: "6%" },
          tdStyle: { width: "6%" },
        },
        {
          key: "status",
          label: "สถานะ",
          sortable: true,
          sortByFormatted: true,
          formatter: value => {
            const order = {
              DRAFT: 0,
              SENT_TO_ERP: 4,
              PARTIALLY_ORDERED: 3,
              CLOSED: 2,
              CANCELED: 1,
            };
            return order[value] ?? 99;
          },
          thStyle: { width: "10%" },
          tdStyle: { width: "10%" },
        },
        {
          key: "no",
          label: "รหัสใบขอซื้อ",
          sortable: true,
          thStyle: { width: "22%" },
          tdStyle: { width: "22%" },
          tdClass: "text-nowrap code-cell",
        },
        {
          key: "mrpNo",
          label: "รหัสใบวางแผนความต้องการวัสดุ",
          sortable: true,
          formatter: value => value || "-",
          thStyle: { width: "25%" },
          tdStyle: { width: "25%" },
          tdClass: "text-nowrap code-cell",
        },
        {
          key: "creationDt",
          label: "วันที่ทำรายการ",
          sortable: true,
          thStyle: { width: "21%" },
          tdStyle: { width: "21%" },
        },
        {
          key: "buttons",
          label: "",
          class: "text-center",
          thStyle: { width: "9%" },
          tdStyle: { width: "9%" },
        },
      ],
    };
  },
  methods: {
    formatDate(date) {
      return date ? moment(date).locale("th").format("DD MMM YY") : "";
    },
    isPrEditable(item) {
      return item.status === PR_STATUS.DRAFT;
    },
    isPrCancelable(item) {
      return (
        item.status === PR_STATUS.DRAFT ||
        item.status === PR_STATUS.SENT_TO_ERP
      );
    },
    sortCompare(a, b, key) {
      if (key === "creationDt") {
        const diff = new Date(a.creationDt) - new Date(b.creationDt);
        if (diff !== 0) return diff;
        return (a.no ?? "").localeCompare(b.no ?? "", undefined, {
          numeric: true,
        });
      }
      return null;
    },
  },
};
</script>

<style scoped>
.add-pr-btn {
  border: 1px solid #5299d6;
  color: #5299d6;
  background: transparent;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.add-pr-btn:hover {
  background-color: #5299d6;
  color: #fff;
}

/* ── Table cell alignment & padding (matches original #prTable styles) ── */
::v-deep table thead tr th,
::v-deep table tbody tr td {
  vertical-align: middle !important;
}

::v-deep table td {
  padding: 16px 24px !important;
  word-break: break-word !important;
  white-space: normal !important;
}

::v-deep table th {
  padding: 16px 24px !important;
  word-break: break-word !important;
  white-space: normal !important;
}

/* Code columns stay on one line even though the table wraps text */
::v-deep table td.code-cell {
  white-space: nowrap !important;
  word-break: normal !important;
}

/* ── Action button group ── */
.action-button-group {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
}

.icon-action {
  font-size: 20px !important;
  cursor: pointer;
  color: #a3a4a6;
}

.icon-info-hover:hover {
  color: #1976d2;
}

.action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  border: none !important;
  background-color: transparent !important;
}

.action-button:disabled,
.action-button--disabled {
  cursor: not-allowed !important;
  color: #ebebeb !important;
}

.action-button:disabled .icon-action,
.action-button--disabled .icon-action {
  cursor: not-allowed !important;
  color: #ebebeb !important;
}
</style>
